import { useCallback, useEffect, useRef, useState } from 'react';
import { useAPICall } from './useAPICall';
import { getShowerMeetingDetails } from '../services/shower-meeting-service';

export const JOIN_POLICY = Object.freeze({ timeout: 15000, delays: [2000, 5000], help: 15000, reload: 45000, maxWait: 30000, attempts: 3 });

function retryDeadline(value, now) {
  if (value == null || String(value).trim() === '') return 0;
  const text = String(value).trim();
  if (/^\d+(\.\d+)?$/.test(text)) return now + Number(text) * 1000;
  const date = Date.parse(text);
  return Number.isFinite(date) ? Math.max(now, date) : 0;
}

function transient(result) {
  if (result.transport) return result.transport === 'network' || result.transport === 'timeout';
  return result.status === 408 || result.status === 429 || (result.status >= 500 && result.status < 600);
}

function sdkMessage(error) {
  const code = String(error?.code || '');
  if (code === '0004' || code === '4') return 'This video link may have expired. Ask the host for a new link.';
  if (code === '0010' || code === '10') return 'This browser is not supported. Try a current version of Chrome or Safari.';
  if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') return 'Camera or microphone access was blocked. Check browser and system permissions, then reload video chat.';
  if (error?.name === 'NotFoundError' || error?.name === 'NotReadableError') return 'A camera or microphone is unavailable. Check your devices and try again; you may also join with them off if supported.';
  return 'We could not connect video. Please reload video chat or ask the host for help.';
}

function leaveObsolete(client) {
  if (client && typeof client.leave === 'function') {
    try { Promise.resolve(client.leave()).catch(() => {}); } catch (_) { /* Do not log upstream errors. */ }
  }
}

export function useVideoJoin(meetingId, authToken, initMeeting, providerClient) {
  const [, , , request] = useAPICall(getShowerMeetingDetails, { onLoad: false });
  const initial = useRef({ started: Date.now(), metadata: null, client: null, meta: 'pending', sdk: 'pending', attempt: 0, deadline: 0, gate: 0, offline: !navigator.onLine, message: '', allowUnthemed: false, useDefaults: false });
  const state = useRef(initial.current);
  const [view, setView] = useState(initial.current);
  const [now, setNow] = useState(Date.now());
  const [handedOff, setHandedOff] = useState(false);
  const handoffComplete = useRef(false);
  const initialHandoff = Boolean((view.metadata || view.useDefaults) && view.client && providerClient === view.client);
  const handoffReady = Boolean((view.metadata || view.useDefaults) && view.client && providerClient && (handedOff || initialHandoff));
  const waitingForProvider = Boolean(view.client && !handedOff && providerClient !== view.client);
  const mounted = useRef(false);
  const generation = useRef(0);
  const timer = useRef(null);
  const controller = useRef(null);
  const initRef = useRef(initMeeting);
  initRef.current = initMeeting;
  const update = useCallback((patch) => {
    state.current = { ...state.current, ...patch };
    if (mounted.current) {
      setNow(Date.now());
      setView(state.current);
    }
  }, []);
  const clearSchedule = useCallback(() => { clearTimeout(timer.current); timer.current = null; }, []);

  const fetchMetadata = useCallback(async () => {
    const s = state.current;
    if (!mounted.current || controller.current || s.metadata || s.useDefaults || s.attempt >= JOIN_POLICY.attempts || s.offline || Date.now() < s.gate) return;
    clearSchedule();
    const attempt = s.attempt + 1;
    const requestController = new AbortController();
    controller.current = requestController;
    const current = generation.current;
    update({ attempt, meta: 'pending', deadline: 0, allowUnthemed: false });
    const result = await request(meetingId, { signal: requestController.signal, timeout: JOIN_POLICY.timeout });
    if (controller.current === requestController) controller.current = null;
    if (!mounted.current || current !== generation.current || requestController.signal.aborted) return;
    if (result.transport === 'cancelled') {
      update({ meta: 'error', message: 'The meeting details request was cancelled. You can try again.', gate: 0 });
      return;
    }
    if (result.status >= 200 && result.status < 300) {
      const details = result.data?.data;
      if (!details || typeof details !== 'object' || Array.isArray(details) ||
          !details.dyteMeetingDetails || typeof details.dyteMeetingDetails !== 'object' || Array.isArray(details.dyteMeetingDetails) ||
          (details.dyteThemeDataDetails != null &&
            (typeof details.dyteThemeDataDetails !== 'object' || Array.isArray(details.dyteThemeDataDetails)))) {
        update({ meta: 'error', message: 'Meeting details were incomplete. Ask the host for a new link.', gate: 0 });
      } else update({ metadata: result, meta: 'ready', message: '', gate: 0 });
      return;
    }
    if (!transient(result) || attempt >= JOIN_POLICY.attempts) {
      update({ meta: 'error', message: transient(result) ? 'We could not load meeting details after three attempts.' : 'We could not load meeting details. Check the link or ask the host for help.', gate: retryDeadline(result.retryAfter, Date.now()), allowUnthemed: result.configurationError !== true });
      return;
    }
    const time = Date.now();
    const gate = retryDeadline(result.retryAfter, time);
    const deadline = Math.max(time + JOIN_POLICY.delays[attempt - 1], gate);
    update({ meta: deadline - time > JOIN_POLICY.maxWait ? 'waiting' : 'scheduled', deadline, gate, message: 'We could not load meeting details yet.', allowUnthemed: true });
  }, [clearSchedule, meetingId, request, update]);

  const startSdk = useCallback(() => {
    // A rejected initializer exposes no client for cleanup. Recovery reloads
    // the document rather than starting a second initializer in this page.
    if (!mounted.current || state.current.sdk !== 'pending') return;
    update({ sdk: 'running', sdkMessage: '' });
    const current = generation.current;
    Promise.resolve().then(() => initRef.current({ authToken })).then((client) => {
      if (!mounted.current || current !== generation.current) {
        // Use the SDK's public leave() only for an obsolete resolved client;
        // a pending initializer is not cancelled.
        if (client && client !== state.current.client) leaveObsolete(client);
        return;
      }
      if (!client) update({ sdk: 'running', sdkMessage: 'Video is still initializing. Reload the page if it does not finish.' });
      else update({ sdk: 'ready', client });
    }, (error) => {
      if (mounted.current && current === generation.current) update({ sdk: 'error', sdkMessage: sdkMessage(error) });
    });
  }, [authToken, update]);

  useEffect(() => {
    mounted.current = true;
    const current = ++generation.current;
    if (!meetingId?.trim() || !authToken?.trim()) update({ meta: 'invalid', sdk: 'invalid', message: 'This video link is incomplete. Ask the host for a new link.' });
    else Promise.resolve().then(() => {
      if (mounted.current && current === generation.current) { fetchMetadata(); startSdk(); }
    });
    const online = () => update({ offline: false });
    const offline = () => update({ offline: true });
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    return () => {
      mounted.current = false;
      generation.current = current + 1;
      const cleanupGeneration = generation.current;
      const client = state.current.client;
      clearSchedule();
      controller.current?.abort();
      controller.current = null;
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
      // StrictMode immediately reinstalls this effect. Only release a client
      // that remained owned by an unmounted, pre-handoff controller.
      Promise.resolve().then(() => {
        if (!mounted.current && generation.current === cleanupGeneration && !handoffComplete.current && state.current.client === client) {
          leaveObsolete(client);
        }
      });
    };
  }, [meetingId, authToken, fetchMetadata, startSdk, update, clearSchedule]);

  useEffect(() => {
    if (initialHandoff) { handoffComplete.current = true; setHandedOff(true); }
  }, [initialHandoff]);

  useEffect(() => {
    if (handedOff || initialHandoff) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [handedOff, initialHandoff]);

  useEffect(() => {
    clearSchedule();
    if (view.meta === 'pending' && view.attempt === 0 && !view.offline && meetingId?.trim() && authToken?.trim()) {
      timer.current = setTimeout(fetchMetadata, 0);
    } else if (view.meta === 'scheduled' && !view.offline) {
      timer.current = setTimeout(fetchMetadata, Math.max(0, view.deadline - Date.now()));
    }
    return clearSchedule;
  }, [view.meta, view.offline, view.deadline, view.attempt, meetingId, authToken, fetchMetadata, clearSchedule]);

  const retryNow = () => {
    if (Date.now() < state.current.gate || state.current.offline) return;
    if (state.current.meta === 'error' || state.current.meta === 'waiting' || state.current.meta === 'stopped') {
      if (state.current.attempt >= JOIN_POLICY.attempts || state.current.meta === 'error') update({ attempt: 0, gate: 0 });
    }
    fetchMetadata();
  };
  const stop = () => { clearSchedule(); update({ meta: 'stopped', deadline: 0 }); };
  const continueUnthemed = () => {
    if (!state.current.allowUnthemed || state.current.metadata || controller.current) return;
    clearSchedule();
    update({ meta: 'fallback', useDefaults: true, allowUnthemed: false, deadline: 0, message: '' });
  };
  return { ...view, handoffReady, waitingForProvider, showHelp: now - view.started >= JOIN_POLICY.help, showReload: now - view.started >= JOIN_POLICY.reload, elapsed: Math.max(0, Math.floor((now - view.started) / 1000)), countdown: Math.max(0, Math.ceil((Math.max(view.deadline, view.gate) - now) / 1000)), gated: now < view.gate, retryNow, stop, continueUnthemed };
}
